import React, { useState, useEffect } from 'react';
import ReportLayout from '../../components/Layout/ReportLayout';
import styles from '../../assets/styles/report/SearchAnalysis.module.scss';
import { motion } from 'framer-motion';
import { useReportFilter } from '../../contexts/ReportContext';
import { useProjectContext } from "../../contexts/ProjectContext";

const SearchAnalysis: React.FC = () => {
  const { selectedProjectId } = useProjectContext();
  const { selectedPeriod, customStart, customEnd } = useReportFilter();

  const [selectedPlatform, setSelectedPlatform] = useState('All');
  const [keywordData, setKeywordData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedProjectId) return;
      setIsLoading(true);
      try {
        const params: Record<string, string> = {
          project_id: String(selectedProjectId),
          period: selectedPeriod,
        };
        if (selectedPeriod === 'custom' && customStart && customEnd) {
          params.start = customStart;
          params.end = customEnd;
        }
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/get-keyword-report?${new URLSearchParams(params)}`,
          { credentials: 'include' }
        );
        const data = await response.json();
        setKeywordData(data);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedProjectId, selectedPeriod, customStart, customEnd]);

  const filteredData = Array.isArray(keywordData)
    ? keywordData.filter(item => selectedPlatform === 'All' || item.platform === selectedPlatform)
    : [];

  return (
    <ReportLayout>
      <div className={styles.reportContent}>

        {/* Header Area */}
        <div className={styles.headerArea}>
          <div>
            <h2 className={styles.pageTitle}>検索広告分析</h2>
            <p className={styles.pageDescription}>キーワードごとの配信実績と効果指標の分析です。</p>
          </div>

          {/* Filters */}
          <div className={styles.filters}>
            {/* Platform Filter */}
            <div className={styles.platformFilter}>
              <span>Platform:</span>
              {['All', 'Google', 'Yahoo', 'Bing'].map(p => (
                <button
                  key={p}
                  onClick={() => setSelectedPlatform(p)}
                  className={`${styles.platformBtn} ${selectedPlatform === p ? styles.active : ''}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Keyword List Header */}
        <div className={styles.listHeader}>
          <div className={styles.colLeft}>Keyword / Platform</div>
          <div>Imp</div>
          <div>Clicks</div>
          <div>CTR</div>
          <div>CPC</div>
          <div>Cost</div>
          <div>CV(micro)</div>
          <div>CVR</div>
        </div>

        {/* Keyword List (Vertical Layout) */}
        <div className={styles.listBody}>
          {isLoading ? (
            <div className={styles.loading}>読み込み中...</div>
          ) : filteredData.map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
              className={styles.listRow}
            >
              {/* Keyword & Platform */}
              <div className={styles.colKeyword}>
                <div className={styles.keywordText}>{item.keyword}</div>
                <span className={styles.platformBadge}>{item.platform}</span>
              </div>
              <div>{item.impressions.toLocaleString()}</div>
              <div>{item.clicks.toLocaleString()}</div>
              <div className={styles.textBlue}>{item.ctr}</div>
              <div>¥{item.cpc.toLocaleString()}</div>
              <div>¥{item.cost.toLocaleString()}</div>
              <div className={styles.textBold}>{item.conversions}</div>
              {/* <div className={styles.textCpa}>¥{item.cpa.toLocaleString()}</div> */}
              <div className={styles.textPink}>{item.cvr}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </ReportLayout>
  );
};

export default SearchAnalysis;
